import React from "react";
import { TrendingUp, TrendingDown, Info } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { formatPercent, formatSignedPercent } from "@/utils/numberFormat";
import type { InstagramEngagementRate } from "./instagram.types";

interface InstagramEngagementRatesProps {
  rates: InstagramEngagementRate[];
  totalPosts: number;
  loading?: boolean;
}

/** Trend indicator for engagement rates (always visible for mock data) */
function EngagementTrend({
  value,
  trend,
}: {
  value: number;
  trend: "up" | "down" | "neutral";
}): React.ReactElement | null {
  if (trend === "neutral" || value === 0) return null;
  const Icon = trend === "up" ? TrendingUp : TrendingDown;
  const color = trend === "up" ? "text-green-500" : "text-red-500";
  return (
    <div className={`flex items-center gap-1 text-sm ${color}`}>
      <Icon className="w-4 h-4" />
      <span>{formatSignedPercent(value, 1)}</span>
    </div>
  );
}

/** Skeleton for the engagement rates + formula section */
function EngagementRatesSkeleton(): React.ReactElement {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <Card key={`eng-sk-${i}`} className="card-kpi-secondary">
            <CardHeader>
              <Skeleton className="h-4 w-40" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="border-dashed border-muted-foreground/30">
        <CardContent className="py-3 px-4">
          <Skeleton className="h-5 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

export default function InstagramEngagementRates({
  rates,
  totalPosts,
  loading = false,
}: InstagramEngagementRatesProps): React.ReactElement {
  if (loading) return <EngagementRatesSkeleton />;

  return (
    <div className="space-y-4">
      {/* 3 Engagement Rate cards */}
      <div className="grid gap-4 md:grid-cols-3">
        {rates.map((rate) => (
          <Card key={rate.title} className="card-kpi-secondary relative">
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-1">
                  <span className="truncate">{rate.title}</span>
                  <HelpTooltip
                    content="Curtidas + Comentários + Salvos"
                    triggerClassName="h-3.5 w-3.5 text-[9px]"
                  />
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-start min-w-0">
                <span className="text-2xl font-light text-foreground">
                  {formatPercent(rate.value)}
                </span>
                <EngagementTrend value={rate.change} trend={rate.trend} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Formula info bar */}
      <Card className="border-dashed border-muted-foreground/30">
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Info className="h-4 w-4 flex-shrink-0" />
            <span>
              <strong>Como o cálculo é realizado?</strong>
            </span>
            <div className="ml-auto flex items-center gap-2 text-xs font-medium text-foreground">
              <div className="flex flex-col items-center border border-border rounded px-3 py-1.5">
                <span className="text-[10px] text-muted-foreground">
                  Curtidas + Comentários + Salvos
                </span>
                <div className="w-full border-t border-border my-1" />
                <span className="text-[10px] text-muted-foreground">
                  Seguidores OU Visualizações OU Alcance
                </span>
              </div>
              <span className="text-base font-semibold">× 100</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
